import { DeleteOutlined } from "@ant-design/icons"
import { Button, Modal, Typography } from "antd"
import { useSetAtom } from "jotai"
import { useCallback, useEffect, useRef, useState } from "react"

import Flex from "#blocks/Flex"
import LinkButton from "#blocks/LinkButton"
import { ROOM_COLORS } from "#constants/classOptions"
import { useUserContext } from "#contexts/UserContextProvider"
import { HELP_REQUEST_PRIORITY } from "#customTypes/composedTypes/helpRequestTypes"

import { helpMenuOpenAtom } from "./helpMenuAtom"
import { getRoomLabel } from "./helpRequestConstants"
import { useHelpRequestContext } from "./HelpRequestContextProvider"
import {
  AcknowledgerList,
  FuzzyTimestamp,
  UrgentTag,
} from "./HelpRequestShared"
import {
  useCanResolveHelpRequest,
  useHelpRequestCreator,
} from "./useHelpRequests"

const { Text } = Typography

const CreatorName = ({ createdBy }: { createdBy: string }) => {
  const user = useHelpRequestCreator(createdBy)
  return <>{user?.name ?? "Unknown"}</>
}

const ResolveButton = ({
  requestId,
  createdBy,
  onResolve,
}: {
  requestId: string
  createdBy: string
  onResolve: (id: string) => void
}) => {
  const canResolve = useCanResolveHelpRequest(createdBy)
  if (!canResolve) return null
  return (
    <Button
      onClick={() => onResolve(requestId)}
      style={{ flex: 1 }}
      danger
      icon={<DeleteOutlined />}
    >
      Resolve
    </Button>
  )
}

const HelpRequestModal = () => {
  const { effectiveUserId } = useUserContext()
  const { sorted, activeRequests, acknowledgeRequest, resolveRequest } =
    useHelpRequestContext()
  const setHelpMenuOpen = useSetAtom(helpMenuOpenAtom)
  const [dismissed, setDismissed] = useState(false)
  const prevCountRef = useRef(activeRequests.length)

  const topRequest = sorted[0] ?? null

  useEffect(() => {
    if (activeRequests.length > prevCountRef.current) {
      setDismissed(false)
    }
    prevCountRef.current = activeRequests.length
  }, [activeRequests.length])

  const handleAcknowledge = useCallback(
    (id: string, currentlyAcked: boolean) =>
      void acknowledgeRequest(id, currentlyAcked),
    [acknowledgeRequest]
  )

  const handleResolve = useCallback(
    (id: string) => {
      void resolveRequest(id)
      setDismissed(true)
    },
    [resolveRequest]
  )

  if (!effectiveUserId || !topRequest) return null

  const isUrgent = topRequest.priority === HELP_REQUEST_PRIORITY.URGENT
  const ackIds = topRequest.acknowledgedByIds ?? []
  const alreadyAcked = ackIds.includes(effectiveUserId)
  const isCreator = effectiveUserId === topRequest.createdBy
  const roomColor = ROOM_COLORS[topRequest.room]

  return (
    <Modal
      open={!dismissed}
      onCancel={() => setDismissed(true)}
      footer={null}
      width={380}
      style={{ top: 30 }}
      destroyOnClose
      closable={false}
      styles={{
        content: { padding: 16 },
        body: { padding: 0 },
        header: { display: "none" },
      }}
    >
      <Flex
        col
        style={{
          background: roomColor,
          borderRadius: 8,
          padding: "20px 20px 16px",
          color: "white",
        }}
      >
        <Flex
          style={{
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div style={{ fontSize: 22, fontWeight: 700 }}>
            <CreatorName createdBy={topRequest.createdBy} />
          </div>
          {isUrgent && <UrgentTag />}
        </Flex>

        <div
          style={{
            fontSize: 14,
            color: "rgba(255,255,255,0.85)",
            marginTop: 2,
          }}
        >
          {getRoomLabel(topRequest.room)}
        </div>

        <div style={{ marginTop: 6 }}>
          <FuzzyTimestamp createdAt={topRequest.createdAt} />
        </div>

        {topRequest.comment && (
          <div
            style={{
              marginTop: 10,
              padding: "8px 12px",
              background: "rgba(255,255,255,0.15)",
              borderRadius: 6,
              fontSize: 14,
            }}
          >
            &ldquo;{topRequest.comment}&rdquo;
          </div>
        )}

        <Flex gap={8} style={{ marginTop: 14 }}>
          {!isCreator && (
            <Button
              type="primary"
              style={{
                flex: 1,
                background: alreadyAcked ? "#52c41a" : "white",
                borderColor: alreadyAcked ? "#52c41a" : "white",
                color: alreadyAcked ? "white" : "rgba(0,0,0,0.88)",
              }}
              onClick={() => handleAcknowledge(topRequest.id, alreadyAcked)}
            >
              {alreadyAcked ? "On My Way ✓" : "On My Way"}
            </Button>
          )}
          <ResolveButton
            requestId={topRequest.id}
            createdBy={topRequest.createdBy}
            onResolve={handleResolve}
          />
        </Flex>

        {ackIds.length > 0 && <AcknowledgerList ids={ackIds} />}
      </Flex>
      {sorted.length > 1 && (
        <Text
          style={{
            display: "block",
            marginTop: 10,
            fontSize: 11,
            textAlign: "center",
          }}
        >
          +{sorted.length - 1} more request{sorted.length > 2 ? "s" : ""} —{" "}
          <LinkButton
            onClick={() => {
              setHelpMenuOpen(true)
              setDismissed(true)
            }}
          >
            Manage in help menu
          </LinkButton>
        </Text>
      )}
    </Modal>
  )
}

export default HelpRequestModal
